import { useEffect, useMemo, useState } from 'react';
import { Loader2, XCircle } from 'lucide-react';
import clsx from 'clsx';
import { CryptoPrivacyBanner } from '../components/CryptoPrivacyBanner';
import { ToolHeader } from '../components/ToolHeader';
import {
  computeHmac,
  type HmacAlgo,
  type IoFormat,
  type OutputFormat,
} from '../lib/hmac';

const ALGOS: ReadonlyArray<HmacAlgo> = ['SHA-1', 'SHA-256', 'SHA-384', 'SHA-512'];
const IO_FORMATS: ReadonlyArray<IoFormat> = ['utf-8', 'hex', 'base64'];
const OUTPUT_FORMATS: ReadonlyArray<OutputFormat> = ['hex', 'base64', 'base64url'];

type State =
  | { kind: 'idle' }
  | { kind: 'computing' }
  | { kind: 'ok'; value: string }
  | { kind: 'error'; error: string };

const DEBOUNCE_MS = 200;

/**
 * `/tools/crypto/hmac` — HMAC-SHA-{1,256,384,512} keyed digest.
 *
 * Layout: messages and keys live side-by-side in two textareas (the
 * key one masked by default since people frequently paste real
 * secrets here). Algorithm + format choices are a single options
 * strip above so users can flip them without scrolling.
 */
export default function HmacTool(): JSX.Element {
  const [message, setMessage] = useState('');
  const [messageFormat, setMessageFormat] = useState<IoFormat>('utf-8');
  const [key, setKey] = useState('');
  const [keyFormat, setKeyFormat] = useState<IoFormat>('utf-8');
  const [algo, setAlgo] = useState<HmacAlgo>('SHA-256');
  const [output, setOutput] = useState<OutputFormat>('hex');
  const [showKey, setShowKey] = useState(false);

  // Compose the request into a single key for memoisation. We use a
  // small debounce because typing into the message textarea would
  // otherwise spawn a SubtleCrypto promise per keystroke.
  const reqKey = useMemo(
    () =>
      JSON.stringify({ message, messageFormat, key, keyFormat, algo, output }),
    [message, messageFormat, key, keyFormat, algo, output],
  );

  const [state, setState] = useState<State>({ kind: 'idle' });

  useEffect(() => {
    if (key.length === 0 && message.length === 0) {
      setState({ kind: 'idle' });
      return;
    }
    setState({ kind: 'computing' });
    let cancelled = false;
    const t = window.setTimeout(() => {
      computeHmac({ message, messageFormat, key, keyFormat, algo, output })
        .then((value) => {
          if (!cancelled) setState({ kind: 'ok', value });
        })
        .catch((e) => {
          if (!cancelled) {
            setState({
              kind: 'error',
              error: e instanceof Error ? e.message : String(e),
            });
          }
        });
    }, DEBOUNCE_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [reqKey, message, messageFormat, key, keyFormat, algo, output]);

  return (
    <div className="flex h-full flex-col gap-4 p-4">
      <ToolHeader
        toolId="hmac"
        category="crypto"
        description="HMAC-SHA-1 / 256 / 384 / 512，消息与密钥均支持 utf-8 / hex / base64。基于 SubtleCrypto，零依赖。"
      />

      <CryptoPrivacyBanner />

      <section className="card-pad flex flex-col gap-3">
        <div className="grid gap-3 lg:grid-cols-2">
          <FormatTextarea
            label="消息"
            value={message}
            onChange={setMessage}
            format={messageFormat}
            onFormatChange={setMessageFormat}
            placeholder="在此输入消息（按选择的编码格式）"
          />
          <FormatTextarea
            label="密钥"
            value={key}
            onChange={setKey}
            format={keyFormat}
            onFormatChange={setKeyFormat}
            placeholder="在此输入密钥"
            mask={!showKey}
            extra={
              <button
                type="button"
                className="text-[11px] text-ink-muted hover:text-accent"
                onClick={() => setShowKey((s) => !s)}
              >
                {showKey ? '隐藏' : '显示'}
              </button>
            }
          />
        </div>

        <div className="flex flex-wrap items-center gap-3 text-xs">
          <SegmentSelect
            label="算法"
            value={algo}
            options={ALGOS}
            onChange={setAlgo}
          />
          <SegmentSelect
            label="输出"
            value={output}
            options={OUTPUT_FORMATS}
            onChange={setOutput}
          />
        </div>
      </section>

      <section className="card-pad flex flex-col gap-2">
        <header className="flex items-center justify-between text-sm font-medium text-ink">
          <span>HMAC-{algo}</span>
          {state.kind === 'computing' && (
            <span className="inline-flex items-center gap-1 text-[11px] text-ink-dim">
              <Loader2 className="h-3 w-3 animate-spin" /> 计算中
            </span>
          )}
        </header>

        {state.kind === 'idle' && (
          <p className="text-[12px] text-ink-dim">— 输入消息和密钥后此处显示 HMAC —</p>
        )}
        {state.kind === 'ok' && <ResultLine value={state.value} />}
        {state.kind === 'error' && (
          <p className="inline-flex items-center gap-1 text-sm text-rose-600 dark:text-rose-400">
            <XCircle className="h-3.5 w-3.5" />
            {state.error}
          </p>
        )}
      </section>
    </div>
  );
}

function FormatTextarea({
  label,
  value,
  onChange,
  format,
  onFormatChange,
  placeholder,
  mask,
  extra,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  format: IoFormat;
  onFormatChange: (f: IoFormat) => void;
  placeholder?: string;
  mask?: boolean;
  extra?: React.ReactNode;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-1">
      <header className="flex items-center justify-between text-xs">
        <span className="font-medium text-ink">{label}</span>
        <div className="flex items-center gap-2">
          {extra}
          <SegmentSelect
            label=""
            value={format}
            options={IO_FORMATS}
            onChange={onFormatChange}
          />
        </div>
      </header>
      <textarea
        className={clsx(
          'input min-h-[110px] font-mono text-[12px]',
          mask && 'tracking-[0.25em]',
        )}
        spellCheck={false}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        style={mask ? ({ WebkitTextSecurity: 'disc' } as React.CSSProperties) : undefined}
      />
    </div>
  );
}

function SegmentSelect<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: ReadonlyArray<T>;
  onChange: (v: T) => void;
}): JSX.Element {
  return (
    <div className="inline-flex items-center gap-1">
      {label && <span className="text-ink-muted">{label}</span>}
      <div className="inline-flex rounded border border-line bg-bg-card overflow-hidden">
        {options.map((opt) => (
          <button
            key={opt}
            type="button"
            className={clsx(
              'px-2 py-0.5 text-[11px] font-mono',
              value === opt
                ? 'bg-accent text-white'
                : 'text-ink-muted hover:bg-bg-soft hover:text-accent',
            )}
            onClick={() => onChange(opt)}
          >
            {opt}
          </button>
        ))}
      </div>
    </div>
  );
}

function ResultLine({ value }: { value: string }): JSX.Element {
  const [done, setDone] = useState(false);
  const onCopy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(value);
      setDone(true);
      window.setTimeout(() => setDone(false), 1000);
    } catch {
      /* ignore */
    }
  };
  return (
    <div className="flex items-center gap-2">
      <code className="min-w-0 flex-1 break-all rounded bg-bg-soft px-2 py-1 font-mono text-[12px] text-ink">
        {value}
      </code>
      <button
        type="button"
        onClick={onCopy}
        className="rounded border border-line px-2 py-1 text-xs text-ink-muted hover:border-accent/50 hover:text-accent"
      >
        {done ? '已复制' : '复制'}
      </button>
    </div>
  );
}
